import { useCallback, useEffect, useRef, useState } from "react";
import { Image as ImageIcon, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useCreateBugReport, type BugSeverity } from "@/hooks/useBugReports";

interface ReportBugDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024;

export function ReportBugDialog({ open, onOpenChange }: ReportBugDialogProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<BugSeverity>("medium");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createBugReport = useCreateBugReport();

  function reset() {
    setTitle("");
    setDescription("");
    setSeverity("medium");
    setScreenshot(null);
    setPreviewUrl(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  // Revoke object URLs on change/unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const acceptFile = useCallback((file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFileError("Screenshot must be a PNG, JPEG, or WebP image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setFileError("Screenshot must be under 10 MB.");
      return;
    }
    setFileError(null);
    setScreenshot(file);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  }, []);

  // Paste-to-attach: when the dialog is open and the user pastes an image,
  // grab it as the screenshot.
  useEffect(() => {
    if (!open) return;
    function onPaste(e: ClipboardEvent) {
      if (!e.clipboardData) return;
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            acceptFile(file);
            e.preventDefault();
            break;
          }
        }
      }
    }
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [open, acceptFile]);

  function clearScreenshot() {
    setScreenshot(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    createBugReport.mutate(
      {
        title,
        description: description || undefined,
        severity,
        screenshot: screenshot ?? undefined,
      },
      {
        onSuccess: () => {
          reset();
          onOpenChange(false);
        },
      },
    );
  }

  function handleOpenChange(next: boolean) {
    if (!next && !createBugReport.isPending) reset();
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Report a bug</DialogTitle>
          <DialogDescription>
            We'll capture the page, browser, and recent activity automatically.
            Add a screenshot if it helps explain the problem (you can paste one in).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bug-title">Title</Label>
            <Input
              id="bug-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short summary of what went wrong"
              required
              maxLength={200}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bug-description">What happened?</Label>
            <Textarea
              id="bug-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What were you trying to do? What did you expect vs. what happened?"
              rows={5}
            />
          </div>

          <div className="space-y-2">
            <Label>Severity</Label>
            <RadioGroup
              value={severity}
              onValueChange={(v) => setSeverity(v as BugSeverity)}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="low" id="sev-low" />
                <Label htmlFor="sev-low" className="font-normal cursor-pointer">Low</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="medium" id="sev-medium" />
                <Label htmlFor="sev-medium" className="font-normal cursor-pointer">Medium</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="high" id="sev-high" />
                <Label htmlFor="sev-high" className="font-normal cursor-pointer">High</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label>Screenshot (optional)</Label>
            {previewUrl ? (
              <div className="relative rounded-md border bg-muted/30 p-2">
                <img
                  src={previewUrl}
                  alt="Screenshot preview"
                  className="max-h-48 w-full rounded object-contain"
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={clearScreenshot}
                  className="absolute right-2 top-2 h-6 w-6"
                  title="Remove screenshot"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground hover:bg-muted/50"
              >
                <ImageIcon className="h-4 w-4" />
                <span>Choose an image or paste one (PNG, JPEG, WebP, max 10 MB)</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) acceptFile(file);
              }}
            />
            {fileError && (
              <p className="text-sm text-destructive">{fileError}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={createBugReport.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || createBugReport.isPending}
            >
              {createBugReport.isPending ? "Submitting…" : "Submit report"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
